import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';
import './IconButton.css';

/** `primary` é o preenchimento quase-preto da ação (o enviar de um compositor), como no `Button`. */
export type IconButtonVariant = 'primary' | 'ghost' | 'secondary' | 'danger';
/** `lg` (36px) alinha com um `Button` md e com os controles de 36px da linha de um compositor. */
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Required: icon-only controls have no visible text. */
  label: string;
  icon: ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  loading?: boolean;
}

/**
 * Square, icon-only action. The `label` becomes the accessible name and the
 * tooltip-less hover affordance. A pseudo-element expands the tap target to 40px
 * on touch widths without enlarging the visual box.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'md', loading = false, disabled, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn('icon-btn', `icon-btn--${variant}`, `icon-btn--${size}`, className)}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 14 : 16} /> : <span className="icon-btn__glyph" aria-hidden="true">{icon}</span>}
    </button>
  );
});
